import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within UserProvider');
  return context;
};

export const userApi = axios.create({ baseURL: `${BACKEND_URL}/api/auth` });

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('user_token');
    if (!savedToken) {
      setLoading(false);
      return;
    }

    const loadSavedProfile = async () => {
      try {
        setToken(savedToken);
        userApi.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        const response = await userApi.get('/profile');
        setUser(response.data);
      } catch {
        localStorage.removeItem('user_token');
        delete userApi.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadSavedProfile();
  }, []);

  const register = async (name, email, phone, password, recaptchaToken) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
      name, email, phone, password, recaptcha_token: recaptchaToken
    });
    return response.data;
  };

  const login = async (email, password, recaptchaToken) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password, recaptcha_token: recaptchaToken || '' });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('user_token', newToken);
    setToken(newToken);
    setUser(userData);
    userApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('user_token');
    delete userApi.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const response = await userApi.put('/profile', data);
    setUser(response.data);
    return response.data;
  };

  const verifyEmail = async (verificationToken) => {
    const response = await axios.get(`${BACKEND_URL}/api/auth/verify-email`, {
      params: { token: verificationToken }
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('user_token', newToken);
    setToken(newToken);
    setUser(userData);
    userApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    return response.data;
  };

  const resendVerification = async (email, recaptchaToken = '') => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {
      email,
      recaptcha_token: recaptchaToken
    });
    return response.data;
  };

  const forgotPassword = async (email, recaptchaToken = '') => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
      email,
      recaptcha_token: recaptchaToken
    });
    return response.data;
  };

  const resetPassword = async (resetToken, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
      token: resetToken,
      password
    });
    return response.data;
  };

  return (
    <UserContext.Provider value={{ user, token, loading, login, register, logout, updateProfile, verifyEmail, resendVerification, forgotPassword, resetPassword, isAuthenticated: !!user }}>
      {children}
    </UserContext.Provider>
  );
};
